import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Manages collapsable sidebar state: which active item was manually closed, and which
 * non-active item was manually opened.
 *
 * An item is considered **active** when `location.pathname.startsWith(itemPathname)`, or when
 * an `extraActive` condition is passed to `isActive` (for items with routes outside their prefix).
 *
 * Rules:
 * - The active collapsable is open by default and stays open even when a non-active one is opened.
 * - The active collapsable can be manually closed by clicking it.
 * - At most one non-active collapsable can be open at a time; opening another replaces it.
 * - On navigation, all state resets — non-active collapsables close and the active one reopens.
 *
 * @returns `isActive(pathname, extraActive?)` — determines whether a collapsable should be open.
 *   Pass directly as the `isActive` prop of `CollapsableListItem`. Use `extraActive` when a
 *   collapsable should also be considered active due to routes outside its `pathname` prefix
 *   (e.g. `isActive={p => isActive(p, p === Routes.Foo ? fooExtraActive : undefined)}`).
 *
 * @returns `toggle(pathname, isActiveItem?)` — pass directly as the `onClick` prop of
 *   `CollapsableListItem`. `isActiveItem` is forwarded automatically by the component via
 *   `anyActiveChild`; it controls whether the toggle closes an active item (stored in
 *   `manuallyClosed`) or opens/closes a non-active item (stored in `openedNonActive`).
 */
const useSidebar = () => {
  const location = useLocation();

  const [manuallyClosed, setManuallyClosed] = useState<string | null>(null);
  const [openedNonActive, setOpenedNonActive] = useState<string | null>(null);

  useEffect(() => {
    setManuallyClosed(null);
    setOpenedNonActive(null);
  }, [location.pathname]);

  const isActive = (pathname: string, extraActive?: boolean): boolean => {
    if (manuallyClosed === pathname) return false;
    if (location.pathname.startsWith(pathname) || extraActive) return true;
    return openedNonActive === pathname;
  };

  const toggle = (pathname: string, isActiveItem?: boolean) => {
    const treatAsActive = isActiveItem ?? location.pathname.startsWith(pathname);
    if (treatAsActive) {
      setManuallyClosed((prev) => (prev === pathname ? null : pathname));
    } else {
      setOpenedNonActive((prev) => (prev === pathname ? null : pathname));
    }
  };

  return { isActive, toggle };
};

export default useSidebar;
